import React, { useCallback, useEffect, useState } from 'react';
import { getStatusSyncBoletos, StatusSyncBoletos } from '../../../api/comissionamentosApi';
import { BANCO_SYNC_HABILITADO } from '../../../api/bancoSync/bancoSyncApi';
import SincronizarBoletosButton from '../../comissionamentos/components/SincronizarBoletosButton';
import ProcessamentoDoRepasse from '../../comissionamentos/components/ProcessamentoDoRepasse';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import '../../../components/como-funciona/help-circle-btn.css';
import '../../../shared/feedback/erro-de-tela.css';
// Esqueleto de página compartilhado — mesmo import explícito das demais telas de config
// (depender de CSS carregado por outra tela é o acoplamento invisível do BUG-V74).
import '../../recepcao/pages/Recepcao.css';
import './css/adminSistema.css';
import '../../../shared/feedback/aviso-de-tela.css';

/**
 * Administração do Sistema → Comissionamentos (comissionamentos/at-01).
 *
 * Uma coisa só: a **sincronização dos boletos do SGA por data de vencimento**, que é a base que o
 * cálculo de repasse consome. O disparo manual existe para quando alguém precisa do dado antes do
 * cron das 04h — a rotina normal não exige ninguém aqui.
 *
 * ⚠️ Papéis: ADMIN e COMISSIONAMENTO, **sem TI** (ADR 0012 — Comissionamentos está fora do
 * alcance dele, telas e rotas). É o primeiro cartão do hub sem TI, e a exceção é deliberada.
 */

const helpSections: ComoFuncionaSecao[] = [
  {
    titulo: '1. O que esta sincronização traz',
    conteudo: (
      <>
        <p>
          Busca no SGA os <strong>boletos com vencimento no mês passado e no mês atual</strong> e atualiza
          a base que os comissionamentos usam para calcular repasse. Cada boleto é atualizado no lugar (não
          duplica): o que já existia é corrigido, o que faltava é criado.
        </p>
        <p>
          Cobre <strong>apenas a base RJ</strong>, que é onde os comissionamentos operam hoje. Boleto de
          outra base não entra nesta sincronização — e, portanto, não aparece nos cálculos destas telas.
        </p>
      </>
    ),
  },
  {
    titulo: '2. Por que por data de vencimento, e não de emissão',
    conteudo: (
      <p>
        O comissionamento raciocina por <strong>vencimento</strong>, e as duas datas não andam juntas: em
        medição de 03/09/2026, boletos que venceram em três dias de agosto tinham sido emitidos ao longo
        de cinco semanas. Uma busca por emissão nunca fecharia um mês de vencimento — sempre faltaria
        alguém. Existe outra sincronização, na Sincronização da Base, que varre por emissão e serve a
        outros usos; as duas gravam no mesmo lugar e não se atrapalham.
      </p>
    ),
  },
  {
    titulo: '3. Quando acontece sozinho',
    conteudo: (
      <p>
        Todo dia às <strong>04h</strong>, automaticamente, sempre com os mesmos dois meses. O horário vem
        antes do recálculo dos repasses (06h), para que o boleto que chegou de madrugada já entre na conta
        do mesmo dia. O botão aqui serve para adiantar isso quando você precisa do dado agora.
      </p>
    ),
  },
  {
    titulo: '4. Por que o botão pode não fazer nada de imediato',
    conteudo: (
      <>
        <p>
          As sincronizações que conversam com o SGA <strong>não rodam ao mesmo tempo</strong> — o sistema
          do SGA não suporta várias buscas simultâneas, e as duas gravariam nas mesmas linhas. Se outra
          estiver em andamento (associados, veículos, eventos ou a busca por emissão), esta é adiada e o
          sistema avisa.
        </p>
        <p>
          Nada se perde quando isso acontece: como a busca refaz os dois meses inteiros toda vez, a
          execução seguinte cobre o que ficou para trás.
        </p>
      </>
    ),
  },
  {
    titulo: '5. O que "Dados atualizados em" significa',
    conteudo: (
      <>
        <p>
          É o fim da <strong>última sincronização concluída</strong>. Uma que esteja rodando agora não
          muda esse horário — ela ainda não trouxe nada.
        </p>
        <p>
          Se aparecer <strong>"não foi possível verificar"</strong>, o problema é a consulta ao status, e
          não a sincronização: os dados podem estar atualizados, mas o sistema não conseguiu confirmar.
          É diferente de <strong>"ainda não executada"</strong>, que é uma resposta de verdade.
        </p>
      </>
    ),
  },
  {
    titulo: '6. Limitações conhecidas',
    conteudo: (
      <>
        <p>
          <strong>Boleto sem veículo vinculado não aparece</strong> na listagem que o SGA devolve — é uma
          limitação do próprio SGA, não do nosso sistema. São poucos e de valor pequeno, e existe uma
          conferência por competência, na Sincronização da Base, que os recupera.
        </p>
        <p>
          <strong>Boleto de associado muito novo pode ficar de fora</strong> de uma execução: se o
          associado ainda não foi sincronizado, o boleto dele é descartado e entra na próxima. O cadastro
          de associados é atualizado aos sábados.
        </p>
      </>
    ),
  },
];

const formatar = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.toLocaleDateString('pt-BR')} às ${d.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};

const ComissionamentosConfigPage: React.FC = () => {
  const [status, setStatus] = useState<StatusSyncBoletos | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [ajudaAberta, setAjudaAberta] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      setStatus(await getStatusSyncBoletos());
      setErro(null);
    } catch {
      // Estado seguro é `erro`, nunca "nunca sincronizado": a segunda é afirmação de negócio,
      // e uma consulta que falhou não autoriza fazê-la (at-60).
      setStatus(null);
      setErro(
        'Não foi possível verificar a última sincronização. Recarregue a página; se persistir, acione a TI.',
      );
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  return (
    <div className="ConteDo recepcao-conte-do">
      <div className="recepcao-header-row" style={{ justifyContent: 'space-between' }}>
        <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          Comissionamentos
          <button
            type="button"
            className="help-circle-btn"
            title="Como Funciona"
            onClick={() => setAjudaAberta(true)}
          >
            ?
          </button>
        </div>
      </div>

      {/* ⚠️ A faixa de erro NÃO substitui a tela: o cartão e o botão continuam ali (BUG-V27). */}
      {erro && (
        <div className="erro-de-tela" role="alert">
          {erro}
        </div>
      )}

      <div className="admin-card">
        <h3>Sincronização de boletos do SGA</h3>
        <p className="admin-hint">
          Busca os boletos com vencimento no mês passado e no mês atual e atualiza a base usada no
          cálculo de repasse. Acontece sozinha todo dia às 04h; use o botão para adiantar.
        </p>

        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginTop: 12 }}>
          <SincronizarBoletosButton onDisparado={carregar} />
          <span style={{ fontSize: 13, color: 'var(--tema-texto-2, #5f6368)' }}>
            {carregando && !status && !erro && 'Verificando...'}
            {!carregando && !erro && status?.ultimaAtualizacao && (
              <>
                Última sincronização concluída em <strong>{formatar(status.ultimaAtualizacao)}</strong>
                {status.ultimoStatus === 'FALHA_PARCIAL' &&
                  ' — terminou com falhas parciais, parte dos boletos pode não ter sido atualizada.'}
              </>
            )}
            {!carregando && !erro && !status?.ultimaAtualizacao && 'Nenhuma sincronização executada ainda.'}
            {status?.emAndamento && ' Uma sincronização está em andamento agora.'}
          </span>
        </div>

        {!BANCO_SYNC_HABILITADO && (
          <p className="aviso-de-tela" style={{ marginTop: 12 }}>
            O disparo manual não está configurado neste ambiente, então o botão fica indisponível. A
            sincronização automática das 04h não depende desta tela e continua acontecendo.
          </p>
        )}
      </div>

      {/* O passo seguinte ao sync: recalcular o repasse sobre o que foi importado (at-04). */}
      <ProcessamentoDoRepasse />

      <ComoFunciona
        isOpen={ajudaAberta}
        onClose={() => setAjudaAberta(false)}
        titulo="Como Funciona - Comissionamentos"
        secoes={helpSections}
      />
    </div>
  );
};

export default ComissionamentosConfigPage;
